import { createNode } from '@opten/dom/create-node';
import { addClass } from '@opten/dom/add-class';
import { getSelectorElementFromElement } from '@opten/dom/get-selector-element-from-element';
import { removeClass } from '@opten/dom/remove-class';
import { addTransitionEndListener } from '@opten/transition/add-transition-end-listener';
import { eventNamespaceFactory } from '@opten/events/event-namespace-factory';
import { parseCssUnit } from '@opten/number/parse-css-unit';
import { getStyles } from '@opten/dom/get-styles';
import { getCss } from '@opten/dom/get-css';
import { getWindowScrollTop } from '@opten/dom/get-window-scroll-top';

const transitionDuration = 600;

export class SidebarOptions {
	bodyElement: HTMLElement;
	itemClassName?: string;
	backdropClassName?: string;
	closeClassName?: string;
	stickyElements?: HTMLElement[];
	breakpoints: {
		maxWidth: number;
	};
}

export class Sidebar {
	private _options: SidebarOptions = {
		bodyElement: undefined,
		itemClassName: 'js-sidebar-item',
		closeClassName: 'js-sidebar-close',
		backdropClassName: 'c-sidebar__backdrop',
		breakpoints: {
			maxWidth: undefined,
		},
	};

	private _navElement: HTMLElement;
	private _linkElements: HTMLElement[];

	// event names
	private _eventNamespaceFn = eventNamespaceFactory('opten.sidebar');

	// cache the scroll to go back to this position
	private _lastScrollPosition: number | null = null;

	constructor(private _element: HTMLElement, options?: SidebarOptions) {
		this._options = { ...this._options, ...options };

		this._navElement = this._element.firstElementChild as HTMLElement;
		this._linkElements = Array.from(
			this._element.getElementsByClassName(this._options.itemClassName)
		) as HTMLElement[];

		this._init();
	}

	private _init() {
		this._linkElements.forEach((link) =>
			link.addEventListener('click', (event) => this._handleClick(event))
		);

		Array.from(
			document.getElementsByClassName(this._options.closeClassName)
		).forEach((close) =>
			close.addEventListener('click', (event) => this._handleClose(event))
		);

		this._handleBreakpointChange();
		window.addEventListener('resize', () => this._handleBreakpointChange());
	}

	private _handleBreakpointChange() {
		// todo: something like isMediaBreakpointDown()?
		if (
			this._options.breakpoints.maxWidth &&
			window.innerWidth < this._options.breakpoints.maxWidth
		) {
			this._element.style.display = 'none';
		} else {
			this._element.style.display = null;

			// Adjust the element so it's actually visible if we have other elements
			// which could be above of it, so we can see it.
			this._adjustElement();
		}
	}

	private _handleClick(event: Event) {
		event.preventDefault();

		const toggler = event.currentTarget as HTMLElement;
		const target = getSelectorElementFromElement(toggler);

		if (target) {
			this._show(target, toggler);
		}
	}

	private _handleClose(event: Event) {
		event.preventDefault();

		const toggler = event.currentTarget as HTMLElement;
		const target = getSelectorElementFromElement(toggler);

		if (target) {
			this._close(target);
		}
	}

	private _show(sidebar: HTMLElement, toggler: HTMLElement) {
		this._eventNamespaceFn(sidebar, 'show');
		this._eventNamespaceFn(toggler, 'show'); // todo: why?

		// todo: is it possible to show the correct view
		// instead of caching the last scroll position?
		// something like adding the scroll as margin/padding/top?
		this._lastScrollPosition = getWindowScrollTop();

		this._adjustOverlay(sidebar);

		addClass(document.body, 'is-c-sidebar-body');
		addClass(this._options.bodyElement, 'is-c-sidebar-zoom-out');

		removeClass(sidebar, 'is-collapsed');
		addClass(this._navElement, 'is-collapsed');

		addClass(this._getBackdrop(), 'is-visible');

		addTransitionEndListener(
			this._options.bodyElement,
			() => {
				this._eventNamespaceFn(sidebar, 'shown');
				this._eventNamespaceFn(toggler, 'shown'); // todo: why?
				this._updateAria(sidebar, true);
			},
			transitionDuration
		);
	}

	private _close(sidebar: HTMLElement) {
		this._eventNamespaceFn(sidebar, 'hide');

		const backdrop = this._getBackdrop();
		const hasBackdrop = backdrop != null && backdrop !== undefined;

		addClass(this._options.bodyElement, 'is-c-sidebar-zoom-in');
		addClass(sidebar, 'is-collapsed');

		if (hasBackdrop) {
			removeClass(backdrop, 'is-visible');
		}

		addTransitionEndListener(
			this._options.bodyElement,
			() => {
				this._eventNamespaceFn(sidebar, 'hidden');

				removeClass(document.body, 'is-c-sidebar-body');
				removeClass(this._options.bodyElement, 'is-c-sidebar-zoom-out');
				removeClass(this._options.bodyElement, 'is-c-sidebar-zoom-in');

				if (hasBackdrop) {
					this._resetOverlay(backdrop);
				}

				removeClass(this._navElement, 'is-collapsed');

				this._updateAria(sidebar, false);

				if (this._lastScrollPosition) {
					window.scrollTo(0, this._lastScrollPosition);
				}
			},
			transitionDuration
		);
	}

	private _getBackdrop(): HTMLElement {
		return document.getElementsByClassName(
			this._options.backdropClassName
		)[0] as HTMLElement;
	}

	private _createBackdrop(sidebar: HTMLElement): HTMLElement {
		const backdrop = createNode(
			`<div class="${this._options.backdropClassName}"></div>`
		);

		backdrop.addEventListener('click', (event) => this._close(sidebar));

		return backdrop;
	}

	private _setScrollbar() {
		// if the body is overflowing, we need to calculate the scrollbar
		if (this._isBodyOverflowing()) {
			const scrollbarWidth = this._getScrollbarWidth();

			this._setScrollbarOfElement(document.body, scrollbarWidth);

			if (this._options.stickyElements && this._options.stickyElements.length) {
				this._options.stickyElements.forEach((sticky) => {
					if (sticky) {
						this._setScrollbarOfElement(sticky, scrollbarWidth);
					}
				});
			}

			// this._element.style.marginRight = `${scrollbarWidth}px`;
			this._options.bodyElement.style.paddingRight = `${scrollbarWidth}px`;
		}
	}

	private _adjustOverlay(sidebar: HTMLElement) {
		// Adjust the overlay so if the document.body has some margins/paddings
		// e.g. to have a fixed header, we need to adjust by that...
		const styles = getStyles(document.body); // cache

		const calculatedMargin = parseCssUnit(
			getCss(document.body, 'margin-top', styles)
		);
		const calculatedPadding = parseCssUnit(
			getCss(document.body, 'padding-top', styles)
		);

		const calculated = calculatedMargin + calculatedPadding;

		if (calculated && calculated > 0) {
			this._options.bodyElement.style.marginTop = `${calculated * -1}px`;
			this._options.bodyElement.style.paddingTop = `${calculated}px`;
		}

		this._setScrollbar();

		this._options.bodyElement.appendChild(this._createBackdrop(sidebar));
	}

	private _adjustElement() {
		// Make the adjustment much nicer, if the navElement
		// is actually collapsed.
		removeClass(this._navElement, 'is-collapsed');

		this._element.style.top = `50%`;
		this._element.style.marginTop = `-${Math.round(
			this._navElement.getBoundingClientRect().height / 2
		)}px`;
	}

	private _resetOverlay(backdrop: HTMLElement) {
		this._options.bodyElement.style.marginTop = null;
		this._options.bodyElement.style.paddingTop = null;

		this._resetScrollbar();

		// todo: kill events?
		backdrop.parentElement.removeChild(backdrop);
	}

	private _setScrollbarOfElement(element: HTMLElement, scrollbarWidth: number) {
		const style = this._getStyleName(element);
		const attributeName = `data-sidebar-${style}`;

		const actual = element.style[style];
		const calculated = parseCssUnit(getCss(element, style));

		element.setAttribute(attributeName, actual);

		element.style[style] = `${calculated + scrollbarWidth}px`;
	}

	private _resetScrollbar() {
		// this._element.style.marginRight = null;
		this._options.bodyElement.style.paddingRight = null;

		if (this._options.stickyElements && this._options.stickyElements.length) {
			this._options.stickyElements.forEach((sticky) => {
				if (sticky) {
					this._resetScrollbarOfElement(sticky);
				}
			});
		}

		this._resetScrollbarOfElement(document.body);
	}

	private _resetScrollbarOfElement(element: HTMLElement) {
		const style = this._getStyleName(element);
		const attributeName = `data-sidebar-${style}`;

		if (element.hasAttribute(attributeName)) {
			const calculated = Number(element.getAttribute(attributeName));

			if (calculated && calculated > 0) {
				element.style[style] = `${calculated}px`;
			} else {
				element.style[style] = null;
			}

			element.removeAttribute(attributeName);
		}
	}

	private _isBodyOverflowing() {
		const rect = document.body.getBoundingClientRect();

		return rect.left + rect.right < window.innerWidth;
	}

	// To achieve nicer animations we set the scroll width to the
	// body element so we don't jumpt around...
	private _getScrollbarWidth() {
		const div = document.createElement('div');
		div.setAttribute(
			'style',
			`position: absolute;
             top: -9999px;
             width: 50px;
             height: 50px;
             overflow: scroll;`
		);
		document.body.appendChild(div);
		const width = div.getBoundingClientRect().width - div.clientWidth;
		document.body.removeChild(div);
		return width;
	}

	private _getStyleName(element: HTMLElement) {
		if (element.style && element.style.position === 'sticky') {
			return 'margin-right';
		}
		return 'padding-right';
	}

	private _updateAria(sidebar: HTMLElement, expanded: boolean) {
		this._linkElements.forEach((link) => {
			const target = getSelectorElementFromElement(link);

			link.setAttribute(
				'aria-expanded',
				(expanded && target === sidebar).toString()
			);
		});
	}
}

export function sidebar(
	element: HTMLElement,
	options?: SidebarOptions
): Sidebar {
	return new Sidebar(element, options);
}
